/**
 * 「ゲームのファイルを入れるだけの小さな画面」（/upload/:projectId/:env?ref=…・pages/UploadPage.tsx）の純粋な判断と上げ方。
 * 2026-10-04 creator/web-upload（founder「Claude と ChatGPT のプラグインにしたい。そうすると Web の Claude とか、Web の ChatGPT からでも
 *   ゲームがアップロードできるようになるでしょ」・研究 docs/research/2026-10-04-web-connectors.md の W8 A）。
 *
 * どこから開くか: AI（MCP の hosting_upload_page・apps/mcp/src/tools/uploadPage.ts の uploadPageUrl）が、URL モードの elicitation の
 *   ボタン（またはリンク）で出す。Web のチャットには手元のファイルが無い = AI が作った zip を本人がダウンロードして、ここに落とすだけ。
 *   MCP は本体の版の一覧で「message = uploadMessage(ref) の ready の版」を見つけて、チャットに「公開しました」を返す。
 * WHY キーを入れる画面（enterKey.ts）と同じ作りか: チャットから出る先を「1 つのことしかできない小さな画面」にする（探させない）。
 *
 * 上げ方は今の hosting の口のまま（本体は変えない・apps/server/src/modules/hosting/routes.ts）:
 *   作成（path + sha256 + size の一覧）→ 足りない物（missing）だけ PUT（生のバイト）→ finalize。
 *   テスト用のアプリ（development）は activate:true = すぐ公開。公開中のアプリ（production）は activate:false = ready まで
 *   （切り替えは MCP が hosting_deploy と同じ確認で行う = 今の確認の流れ）。公開中のアプリで公開がオフ（403 feature_disabled）なら、
 *   下書きだけオフのまま作る（allowHostingOff・hosting_deploy と同じ印 = 「オンにして公開」は MCP の 1 回の確認で）。
 *   キーらしい文字列の検査（secretScan・保存したキーの指紋）は finalize で本体が今どおり行う（422 secret_like_content）。
 * 依存なし（node --test を型の除去だけで回すため）。本体への通信は UploadApi で受ける（画面は api.ts の api を渡す）。
 */

/** App.tsx のルート。MCP の uploadPageUrl と同じ形（uploadFiles.test.ts が MCP のソースと突き合わせる） */
export const UPLOAD_PATH = '/upload/:projectId/:env';
/** 印の形（MCP の uploadPage.ts の REF_RE と同じ）。12 バイトの乱数の base64url = 16 文字 */
export const UPLOAD_REF_RE = /^[A-Za-z0-9_-]{12,40}$/;

export type UploadEnv = 'development' | 'production';

export interface UploadTarget {
  projectId: string;
  env: UploadEnv;
  ref: string;
}

/** 版のメモ（印つき）。MCP の uploadPageMessage と同じ形（テストが突き合わせる）。版の一覧に出る短い英語の文 */
export const uploadMessage = (ref: string) => `Added on the upload page (${ref})`;

/** URL から入れる先を読む。どれかがおかしければ null（画面は「リンクが正しくありません」） */
export function readUpload(params: { projectId?: string; env?: string }, search: string): UploadTarget | null {
  const projectId = params.projectId ?? '';
  if (!/^[A-Za-z0-9_-]{1,64}$/.test(projectId)) return null;
  if (params.env !== 'development' && params.env !== 'production') return null;
  const ref = new URLSearchParams(search).get('ref') ?? '';
  if (!UPLOAD_REF_RE.test(ref)) return null;
  return { projectId, env: params.env, ref };
}

export interface PickedFile {
  path: string;
  bytes: Uint8Array;
}

export type NormalizeResult = { ok: true; files: PickedFile[]; skipped: number; totalBytes: number } | { ok: false; issue: 'empty' | 'no_index'; skipped: number };

/**
 * 送らない物（パスの区切りごとに見る）。
 *   __MACOSX / .DS_Store / Thumbs.db / desktop.ini = Mac・Windows が zip やフォルダに勝手に入れるごみ
 *   .git = 作業の履歴（大きい・中身を公開しない）・node_modules = 部品の置き場（公開するゲームには要らない・とても多い）
 *   .env* = キーを書いておくファイル（本体の検査でも止まるが、そもそも送らない = 本人が気づかずに出す道を作らない）
 * WHY ドットで始まる物を全部は外さないか: .well-known のように公開に要る物がある
 */
const JUNK_SEGMENT = /^(?:__MACOSX|\.DS_Store|Thumbs\.db|desktop\.ini|\.git|node_modules|\.env(?:\..*)?)$/i;

/**
 * 落とされたファイルを、公開するサイトの形に整える。
 *   - 区切りは /（Windows の \ を直す）・先頭の ./ と / を外す・.. や空の段のあるパスは通さない（数えて外す）
 *   - ごみ（JUNK_SEGMENT）を外す
 *   - 全部が 1 つのフォルダの中にあり、いちばん上に index.html が無ければ、そのフォルダを外す（何段でも）。
 *     WHY: チャットの AI や OS の「圧縮」は「my-game/index.html」のようにフォルダごと zip にすることが多い。そのまま出すと
 *     https://…/my-game/ にしか出ず、いちばん上の URL が「見つかりません」になる
 *   - 同じパスが 2 回来たら後の物
 *   - 何も残らない → empty・いちばん上に index.html が無い → no_index（公開しても最初の画面が出ない）
 */
export function normalizeUpload(input: PickedFile[]): NormalizeResult {
  let skipped = 0;
  const byPath = new Map<string, PickedFile>();
  for (const f of input) {
    const parts = f.path.replace(/\\/g, '/').replace(/^(?:\.\/|\/)+/, '').split('/');
    if (parts.some((p) => p === '' || p === '.' || p === '..') || parts.some((p) => JUNK_SEGMENT.test(p))) {
      skipped++;
      continue;
    }
    byPath.set(parts.join('/'), { path: parts.join('/'), bytes: f.bytes });
  }
  let files = [...byPath.values()];
  if (!files.length) return { ok: false, issue: 'empty', skipped };
  for (;;) {
    if (files.some((x) => x.path === 'index.html')) break;
    const tops = new Set(files.map((x) => x.path.split('/')[0]));
    if (tops.size !== 1 || files.some((x) => !x.path.includes('/'))) break;
    files = files.map((x) => ({ ...x, path: x.path.slice(x.path.indexOf('/') + 1) }));
  }
  if (!files.some((x) => x.path === 'index.html')) return { ok: false, issue: 'no_index', skipped };
  return { ok: true, files, skipped, totalBytes: files.reduce((n, x) => n + x.bytes.length, 0) };
}

/** 本体への通信（画面は api.ts を包んで渡す・テストは偽物）。失敗は { status, code, details } を持つ例外（api.ts の ApiError） */
export interface UploadApi {
  json(path: string, init: { method: 'POST'; body: unknown }): Promise<unknown>;
  raw(path: string, bytes: Uint8Array): Promise<void>;
}

/** 同時に送る数。WHY 4: ブラウザの 1 つの相手への同時の接続（HTTP/1.1 で 6）より少なく・スマホの回線でも詰まらない */
const PUT_CONCURRENCY = 4;

/**
 * 上げる（作成 → 足りない物だけ PUT → finalize）。onProgress(送ったバイト, 送るバイトの合計)。
 * 返す url はテスト用のアプリなら公開された URL（公開中のアプリは切り替え前 = 画面では使わない）
 */
export async function uploadAll(
  deps: { api: UploadApi; sha256: (b: Uint8Array) => Promise<string>; onProgress?: (done: number, total: number) => void },
  t: UploadTarget,
  files: PickedFile[]
): Promise<{ version: number; url: string | null }> {
  const base = `/v1/projects/${t.projectId}/environments/${t.env}`;
  const listed = await Promise.all(files.map(async (f) => ({ path: f.path, sha256: await deps.sha256(f.bytes), size: f.bytes.length, bytes: f.bytes })));
  const body = { files: listed.map(({ path, sha256, size }) => ({ path, sha256, size })), message: uploadMessage(t.ref) };
  let created: { version: number; missing?: string[] };
  try {
    created = (await deps.api.json(`${base}/deployments`, { method: 'POST', body })) as typeof created;
  } catch (e) {
    // 公開中のアプリで公開がオフ: 下書きだけオフのまま作る（オンにするのは MCP の 1 回の確認で）。テスト用のアプリは回り道しない（AI に頼む）
    if (t.env !== 'production' || (e as { code?: string }).code !== 'feature_disabled') throw e;
    created = (await deps.api.json(`${base}/deployments`, { method: 'POST', body: { ...body, allowHostingOff: true } })) as typeof created;
  }
  const bySha = new Map(listed.map((f) => [f.sha256, f.bytes]));
  const missing = [...new Set(created.missing ?? [])].filter((s) => bySha.has(s));
  const total = missing.reduce((n, s) => n + bySha.get(s)!.length, 0);
  let done = 0;
  deps.onProgress?.(0, total);
  const queue = [...missing];
  await Promise.all(
    Array.from({ length: Math.min(PUT_CONCURRENCY, queue.length) }, async () => {
      for (let s = queue.shift(); s !== undefined; s = queue.shift()) {
        const bytes = bySha.get(s)!;
        await deps.api.raw(`${base}/blobs/${s}`, bytes);
        done += bytes.length;
        deps.onProgress?.(done, total);
      }
    })
  );
  const fin = (await deps.api.json(`${base}/deployments/${created.version}/finalize`, { method: 'POST', body: { activate: t.env === 'development' } })) as { url?: unknown };
  return { version: created.version, url: typeof fin.url === 'string' ? fin.url : null };
}

/** 本体のエラー → この画面の言い方（AI に頼む言い方）。null = 共通の errorText */
export function uploadErrorKey(code: string): string | null {
  switch (code) {
    case 'feature_disabled':
      return 'upload.error.off';
    case 'secret_like_content':
      return 'upload.error.keyFound';
    case 'rate_limited':
      return 'upload.error.rateLimited';
    case 'too_many_pending_deployments':
      return 'upload.error.tooMany';
    default:
      return null;
  }
}

/** 422 secret_like_content の details.findings から、見つかったファイルの名前だけ（値は本体も返さない）。最大 5 件 */
export function keyFoundFiles(details: Record<string, unknown> | undefined): string[] {
  const f = details?.findings;
  if (!Array.isArray(f)) return [];
  const names = f.map((x) => (x && typeof (x as { path?: unknown }).path === 'string' ? String((x as { path: string }).path) : null)).filter((x): x is string => !!x);
  return [...new Set(names)].slice(0, 5);
}

/** 大きさの表示（KB / MB・1 桁）。画面の「3 ファイル・1.2 MB」 */
export function sizeText(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
